import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	userSession?: Api.UserSessionContext;
	isAdmin?: boolean;
}

interface IState {
	error?: string;
	isProcessing: boolean;
	shouldRedirect: boolean;
	redirectPath?: string;
}

/**
 * OAuth Callback Handler
 *
 * This component handles the OAuth redirect from WorkOS after Google authentication.
 * It extracts tokens from the URL fragment, stores them securely, and redirects to the dashboard.
 *
 * Security features:
 * - Validates tokens before storage
 * - Clears tokens from browser history
 * - Handles errors gracefully
 * - Uses existing credential store pattern
 */
class _OAuthCallback extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			error: undefined,
			isProcessing: true,
			shouldRedirect: false,
		};
	}

	public componentDidMount() {
		this.handleOAuthCallback();
	}

	private handleOAuthCallback = async () => {
		try {
			// Check for error in query params first
			const queryParams = new URLSearchParams(this.props.location.search);
			const errorParam = queryParams.get('error');

			if (errorParam) {
				this.setState({
					error:
						errorParam === 'authentication_failed'
							? 'Authentication failed. Please try again.'
							: 'An error occurred during authentication.',
					isProcessing: false,
				});
				return;
			}

			// Extract authorization code from query string (within the hash-based route)
			// URL format: https://localhost:3000/#/applinks/launch/auth/oauth-callback?code=...
			const search = this.props.location.search;
			if (!search || search.length <= 1) {
				this.setState({
					error: 'No authentication data received. Please try again.',
					isProcessing: false,
				});
				return;
			}

			// Parse the query string (remove leading ?)
			const searchParams = new URLSearchParams(search.substring(1));
			const code = searchParams.get('code');

			// Validate required code
			if (!code) {
				this.setState({
					error: 'No authorization code received. Please try again.',
					isProcessing: false,
				});
				return;
			}

			// Exchange authorization code for tokens via the API
			const { userSession } = this.props;
			const apiBaseUrl = userSession.webServiceHelper.baseUrl;
			// Construct redirect_uri to match what was sent during authorization
			// For admin.html paths (e.g., https://staging.levitate.ai/admin.html), preserve the path
			const baseUrl = window.location.href.split('#')[0]; // Everything before the hash
			const redirectUri = `${baseUrl}#/applinks/launch/auth/oauth-callback`;

			const tokenResponse = await fetch(`${apiBaseUrl}/oauth/public/v1/token`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: new URLSearchParams({
					grant_type: 'authorization_code',
					code,
					client_id: import.meta.env.VITE_PROGRAM_IDENTIFIER,
					redirect_uri: redirectUri,
				}),
			});

			if (!tokenResponse.ok) {
				const errorText = await tokenResponse.text();
				console.error('Token exchange failed:', errorText);
				this.setState({
					error: 'Failed to complete authentication. Please try again.',
					isProcessing: false,
				});
				return;
			}

			const tokenData = await tokenResponse.json();

			// Validate token response
			if (!tokenData.access_token || !tokenData.refresh_token) {
				this.setState({
					error: 'Invalid token response received. Please try again.',
					isProcessing: false,
				});
				return;
			}

			// Calculate token expiration time (UTC) - use expiresUtc from response if available
			const expiresUtc = tokenData.expiresUtc || Math.floor(Date.now() / 1000) + (tokenData.expires_in || 3600);

			// Build credential object
			const credential: Api.ICredential = {
				access_token: tokenData.access_token,
				refresh_token: tokenData.refresh_token,
				token_type: tokenData.token_type || 'Bearer',
				expires_utc: expiresUtc,
			};

			// Store credential using the existing credential store
			const credentialStore = userSession.webServiceHelper.getCredentialStore();
			await credentialStore.setCredential(credential);

			// Load the user session with the new credential
			await userSession.load();

			// Clear the tokens from browser history for security
			// Use replaceState to avoid leaving tokens in history
			// For hash-based routing, we need to keep the hash route but remove the query params
			// Determine the correct redirect path based on app type (admin vs web)
			const redirectPath = this.props.isAdmin ? '/accounts' : '/dashboard';

			if (window.history.replaceState) {
				const cleanUrl = `${window.location.pathname}#${redirectPath}`;
				window.history.replaceState({}, document.title, cleanUrl);
			}

			// Success - redirect to appropriate dashboard
			this.setState({
				isProcessing: false,
				shouldRedirect: true,
				redirectPath,
			});
		} catch (error) {
			console.error('OAuth callback error:', error);
			this.setState({
				error: 'Failed to complete authentication. Please try again.',
				isProcessing: false,
			});
		}
	};

	public render() {
		const { error, isProcessing, shouldRedirect, redirectPath } = this.state;

		// Redirect to dashboard on success
		if (shouldRedirect && this.props.userSession?.isAuthenticated) {
			return <Redirect to={redirectPath || '/dashboard'} />;
		}

		// Show error state
		if (error) {
			return (
				<div className={css(styleSheet.errorContainer)}>
					<h2>Authentication Error</h2>
					<p>{error}</p>
					<a href='/login' className={css(styleSheet.returnToLoginLink)}>
						Return to Login
					</a>
				</div>
			);
		}

		// Show loading state while processing
		if (isProcessing) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='login' />
					<p className={css(styleSheet.text)}>Completing authentication...</p>
				</div>
			);
		}

		// Fallback - shouldn't normally reach here
		return <Redirect to='/login' />;
	}
}

const OAuthCallbackObserver = observer(_OAuthCallback);

export const OAuthCallback = withRouter(OAuthCallbackObserver);
